import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { axios } from "@/client/libs/axios";
import { useUser } from "@/client/services/user";

const rewardSchema = z.object({
  id: z.string().optional(),
  code: z.string(),
  kind: z.enum(["download", "plan", "ai_credits"]),
  costPoints: z.number().int().positive(),
  quantity: z.number().int().nonnegative().nullish(),
  aiCredits: z.number().int().nonnegative().nullish(),
  planCode: z.string().nullish(),
  isActive: z.boolean().nullish(),
  createdAt: z.string().or(z.date()).nullish(),
  updatedAt: z.string().or(z.date()).nullish(),
});

// NestJS/Prisma peut renvoyer createdAt comme objet Date JS
// (pas string) selon les intercepteurs actifs. On coerce les deux.
const coerceDateToISOString = z
  .union([z.string(), z.date()])
  .transform((v) => (v instanceof Date ? v.toISOString() : v));

const loyaltyEntrySchema = z.object({
  id: z.string(),

  type: z.enum([
    "PURCHASE",
    "CREATE_RESUME",
    "COMPLETE_PROFILE",
    "REFERRAL_ACTIVATED",
    "REVIEW_VERIFIED",
    "REDEMPTION",
    "RESUME_DOWNLOADED",
  ]),

  points: z.number().int(),
  balanceAfter: z.number().int().nonnegative(),
  createdAt: coerceDateToISOString,
  policyVersion: z.string().nullable(),
});

const loyaltyResponseSchema = z.object({
  balance: z.number().int().nonnegative(),

  policyVersion: z.string(),

  rewards: z.array(rewardSchema),

  entries: z.array(loyaltyEntrySchema),

  pagination: z.object({
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
    total: z.number().int().nonnegative(),
  }),
});

export type LoyaltyResponse = z.infer<typeof loyaltyResponseSchema>;

export type LoyaltyReward = z.infer<typeof rewardSchema>;

export type LoyaltyEntry = z.infer<typeof loyaltyEntrySchema>;

export const useLoyaltyDashboard = (page = 1) => {
  const { user } = useUser();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["user-space", "loyalty", user?.id, page],

    enabled: Boolean(user?.id),

    queryFn: async ({ signal }) => {
      const response = await axios.get<unknown>("/engagement/loyalty", {
        params: { page },
        signal,
      });

      try {
        return loyaltyResponseSchema.parse(response.data);
      } catch (error) {
        console.error("Zod Validation Error:", error);
        throw error;
      }
    },

    staleTime: 10_000,
    refetchOnWindowFocus: true,
  });

  const redeemMutation = useMutation({
    mutationFn: async (rewardCode: string) => {
      const response = await axios.post("/engagement/loyalty/redeem", {
        rewardCode,
        requestId: crypto.randomUUID(),
      });

      return response.data;
    },

    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["user-space", "loyalty", user?.id],
        }),

        queryClient.invalidateQueries({
          queryKey: ["user-space", "account-overview", user?.id],
        }),
      ]);
    },
  });

  return {
    data: query.data,

    loading: query.isPending,
    refreshing: query.isFetching,

    error: query.error,

    refetch: query.refetch,

    redeem: (rewardCode: string) => redeemMutation.mutateAsync(rewardCode),

    redeeming: redeemMutation.isPending,

    redeemingReward: redeemMutation.variables,
  };
};
